/**
 * Dagger tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const daggerTattoosPack: ToilePack = {
    slug: 'dagger-tattoos',
    category: 'tattoo',
    name: 'Dagger tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'heart', label: 'Dagger and heart', subject: 'a dagger driven down through a heart, three drops falling from the point below'},
        {key: 'ornate', label: 'Ornate dagger', subject: 'a long ornate dagger pointing down, the hilt curled with filigree and set with one round jewel'},
        {key: 'crossed', label: 'Crossed daggers', subject: 'two daggers crossed in an X, blades up, their guards overlapping in the middle'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin'},
    ],
    directions: ['a rose', 'red ink', 'calf'],
    directionHint: 'try: a rose',
    seo: {
        title: 'Dagger tattoo designs: dagger and heart, ornate, crossed',
        metaDescription: 'Nine dagger tattoo designs: a dagger through a heart, an ornate dagger and two crossed daggers, each in three styles.',
        h1: 'Dagger tattoo: dagger through a heart, ornate dagger, crossed daggers',
        tagline: 'Three dagger designs, each drawn in traditional, fine line and dotwork.',
        intro: 'Nine dagger tattoo designs on white paper: a dagger driven down through a heart with three drops falling, a long ornate dagger with a filigree hilt set with a jewel, and two daggers crossed in an X. The dagger is a staple of tattoo flash, worn for courage, for betrayal survived or for a love that cuts, often on the forearm or the calf. Type a different blade or a twist, such as a rose.',
        useCases: [
            'a classic flash piece for the forearm',
            'an ornate dagger for the calf',
            'a crossed pair for a shoulder or chest',
            'a design sheet to show the artist',
        ],
        faq: [
            {q: 'What do the nine designs show?', a: 'Three dagger subjects drawn three ways: a dagger through a heart, an ornate dagger and two crossed daggers, in traditional, fine line and dotwork.'},
            {q: 'Can I ask for a different blade or a twist?', a: 'Yes. The direction field takes any subject or twist, such as a rose; a typed direction is a request, so the result may differ from the samples.'},
            {q: 'What is the difference between the three styles?', a: 'Traditional has bold black outlines, heavy shading and a limited flat palette; fine line is one uniform hairline with almost no shading; dotwork is a thin outline with all shading built from stippled dots.'},
            {q: 'Are these photographs of tattoos on skin?', a: 'No. Each design is a line drawing on a plain white background, with no skin, arm or body in it.'},
        ],
    },
}
